import type { ChatStatus } from 'ai'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input'

interface ChatInputProps {
  onSend: (text: string) => void
  onStop: () => void
  status: ChatStatus
  disabled?: boolean
  variant?: 'welcome' | 'chat'
}

export function ChatInput({ onSend, onStop, status, disabled, variant = 'chat' }: ChatInputProps) {
  const handleSubmit = ({ text }: { text: string }) => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed)
  }

  const isWelcome = variant === 'welcome'

  return (
    <motion.div
      layoutId="chat-input"
      className={cn(
        isWelcome
          ? 'w-full max-w-2xl mx-auto'
          : 'border-t border-border/40 bg-background px-6 py-4'
      )}
    >
      <div className={cn(!isWelcome && 'max-w-3xl mx-auto')}>
        {isWelcome ? (
          <div className="relative rounded-2xl p-[1px] overflow-hidden">
            {/* Animated gradient border */}
            <div className="absolute inset-0 rounded-2xl animate-gradient-border bg-[length:300%_300%] bg-[linear-gradient(135deg,var(--color-primary),var(--color-primary)/0.3,var(--color-primary)/0.6,var(--color-primary)/0.1,var(--color-primary))]" />
            <div className="relative rounded-2xl bg-background">
              <PromptInput
                onSubmit={handleSubmit}
                className="[&_[data-slot=input-group]]:rounded-2xl [&_[data-slot=input-group]]:border-0 [&_[data-slot=input-group]]:shadow-none"
              >
                <PromptInputTextarea
                  placeholder="Describe your access control policy..."
                  disabled={disabled}
                />
                <PromptInputFooter>
                  <PromptInputTools />
                  <PromptInputSubmit status={status} onStop={onStop} />
                </PromptInputFooter>
              </PromptInput>
            </div>
          </div>
        ) : (
          <PromptInput onSubmit={handleSubmit}>
            <PromptInputTextarea
              placeholder="Describe your access control policy..."
              disabled={disabled}
            />
            <PromptInputFooter>
              <PromptInputTools />
              <PromptInputSubmit status={status} onStop={onStop} />
            </PromptInputFooter>
          </PromptInput>
        )}
        <p className="text-[11px] text-muted-foreground/50 text-center mt-2.5">
          poli-x AI generates XACML 3.0 policies. Always review before deploying.
        </p>
      </div>
    </motion.div>
  )
}
